import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import {
  buildPersonaScriptShareUrl,
  formatPersonaScriptForCopy,
} from "@/features/persona-opportunities/lib/persona-script-clipboard";

async function writeClipboard(text: string): Promise<boolean> {
  const copied = navigator.clipboard?.writeText(text);
  if (!copied) return false;
  try {
    await copied;
    return true;
  } catch {
    return false;
  }
}

/** Share-link and full-script copy used by the workspace footer and the hub drawer header. */
export function usePersonaScriptClipboard(script: PersonaScriptDto) {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = buildPersonaScriptShareUrl(script.id, window.location.origin);
    window.open(url, "_blank", "noopener,noreferrer");
    if (!(await writeClipboard(url))) {
      toast.error(
        t(
          "personaOpportunities.scripts.actions.shareError",
          "Couldn't copy the share link. Please try again.",
        ),
      );
      return;
    }
    setLinkCopied(true);
    toast.success(
      t(
        "personaOpportunities.scripts.actions.shareToast",
        "Preview opened — link copied to clipboard",
      ),
    );
  };

  const copy = async () => {
    const untitled = t("personaOpportunities.scripts.untitledHook", {
      defaultValue: "Script {{slot}}",
      slot: script.slotIndex,
    });
    if (!(await writeClipboard(formatPersonaScriptForCopy(script, untitled)))) {
      toast.error(
        t(
          "personaOpportunities.scripts.actions.copyError",
          "Couldn't copy the script. Please try again.",
        ),
      );
      return;
    }
    setCopied(true);
    toast.success(
      t(
        "personaOpportunities.scripts.actions.copyToast",
        "Script copied to clipboard",
      ),
    );
  };

  return { linkCopied, copied, share, copy };
}
